import { RestError } from '@azure/core-http';
import { CourierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { useMutationEditCourier } from '../../../couriers.queries';
import {
  CourierFormState,
  LoginTab,
  inferLoginTab,
  makeCourierFormConfig,
  mapCourierFormToEditDto,
  mapCourierToForm
} from '../../../logic/courier-form.map';

export type EditCourierActionState = {
  saving: boolean;
  error?: RestError | Error;
};

export interface EditCourierActionProps {
  courier: CourierDto;
  /** Scopes the mobile-number formatter to the project's country. */
  projectID?: string;
  onAbort(): void;
  onSuccess(): void;
}

export function useEditCourierAction({ courier, onAbort, onSuccess }: EditCourierActionProps) {
  const [state, setState] = useImmer<EditCourierActionState>({ saving: false });

  // The courier is already loaded by the details page, so the form opens populated rather than
  // fetching again — and on the tab for the credential the courier actually signs in with.
  const { control, handleSubmit } = useForm<CourierFormState>({ defaultValues: mapCourierToForm(courier) });
  const [activeLoginTab, setActiveLoginTab] = useState<LoginTab>(inferLoginTab(courier));

  const formConfig = makeCourierFormConfig(activeLoginTab);

  const editCourierMutation = useMutationEditCourier(courier.id);

  const handlers = {
    onCancel: onAbort,
    onSubmit(formState: CourierFormState) {
      const changes = mapCourierFormToEditDto(formState, courier, activeLoginTab);

      // Nothing was touched, so there is nothing to send — close rather than issuing an empty PATCH.
      if (Object.keys(changes).length === 0) {
        onAbort();
        return;
      }

      setState((draft) => {
        draft.saving = true;
      });
      editCourierMutation.mutate(changes, {
        onSuccess() {
          onSuccess();
        },
        onError(error) {
          setState((draft) => {
            draft.saving = false;
            draft.error = castDraft(error);
          });
        }
      });
    }
  };

  return { state, handlers, control, handleSubmit, formConfig, activeLoginTab, setActiveLoginTab } as const;
}
